import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clapperboard, type LucideIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { cn } from "@/lib/utils";

export interface VideoPlanningLoaderMessage {
  key?: string;
  fallback: string;
}

interface VideoPlanningLoaderProps {
  title: string;
  subtitle?: string;
  messages: VideoPlanningLoaderMessage[];
  icon?: LucideIcon;
  className?: string;
}

export function VideoPlanningLoader({
  title,
  subtitle,
  messages,
  icon: Icon = Clapperboard,
  className,
}: VideoPlanningLoaderProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const [messageIdx, setMessageIdx] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;
    const id = window.setInterval(() => {
      setMessageIdx((index) => (index + 1) % messages.length);
    }, 3000);
    return () => window.clearInterval(id);
  }, [messages.length]);

  const message = messages[messageIdx] ?? messages[0];
  const resolvedMessage = subtitle ?? (message?.key ? dictionary[message.key]?.[lang] : null) ?? message?.fallback ?? "";
  const motionKey = subtitle ? `hint-${subtitle}` : `msg-${messageIdx}`;

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-6 bg-gradient-to-br from-blue-50 via-white to-purple-50 px-8 py-12 text-center",
        className
      )}
    >
      <div className="relative h-24 w-24">
        <div
          className="absolute inset-0 rounded-full opacity-40 blur-2xl"
          style={{ background: "var(--gradient-fluxi)" }}
        />
        <div className="absolute inset-1 rounded-full border-2 border-slate-200/70" />
        <div className="absolute inset-1 rounded-full border-2 border-transparent border-t-fluxi animate-spin" />
        <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-fluxi shadow-[0_14px_36px_rgba(67,104,229,0.18)]">
          <Icon className="h-7 w-7" strokeWidth={1.9} />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="whitespace-pre-line text-[26px] font-semibold leading-tight text-foreground">{title}</h2>
        <AnimatePresence mode="wait">
          <motion.p
            key={motionKey}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="text-sm text-muted-foreground"
          >
            {resolvedMessage}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
